import React, { useState } from "react";
import { Select } from "antd";
import { Icon } from "Components";
import Alert from "../../components/Alert";
import StepFooter from "../CampaignSteps/components/StepFooter";
import RoleRecipients from "./RoleRecipients";
import PropertyRecipients from "./PropertyRecipients";
import OrganizationRecipients from "./OrganizationRecipients";
import GroupRecipients from "./GroupRecipients";

interface StepProps {
  onBack: () => void;
  onNext: () => void;
  isMobile: boolean
}

type SelectionMethod =
  | "organization"
  | "property"
  | "role"
  | "group"
  | "individual"
  | "";

interface SelectorOption {
  key: SelectionMethod;
  icon: React.ReactNode;
  label: string;
  description: string;
}

const selectionOptions: SelectorOption[] = [
  {
    key: "organization",
    icon: <Icon type="recipientBuilding" />,
    label: "By Organization",
    description: "Send to all users in your organization",
  },
  {
    key: "property",
    icon: <Icon type="recipientProperty" />,
    label: "By Property",
    description: "Select entire properties",
  },
  {
    key: "role",
    icon: <Icon type="recipientRole" />,
    label: "By Role",
    description: "Send to all users assigned to a specific role",
  },
  {
    key: "group",
    icon: <Icon type="recipientGroup" />,
    label: "By Group",
    description: "Select groups or departments",
  },
  {
    key: "individual",
    icon: <Icon type="recipientIndividual" />,
    label: "By Individual",
    description: "Select specific individuals by name or email",
  },
];

const roleOptions = [
  {
    key: "staff",
    label: "Staff",
    count: 40,
    description: "All users other than Managers and Leadership team",
  },
  {
    key: "manager",
    label: "Manager",
    count: 5,
    description: "Department and property managers",
  },
  {
    key: "leadership",
    label: "Leadership and IT",
    count: 2,
    description: "Those overseeing system and strategy",
  },
];

const propertyOptions = [
  { value: "premier-inn",      label: "Premier Inn",            count: 520 },
  { value: "plaza-hotel",      label: "Plaza Hotel",            count: 480 },
  { value: "royal-sunset",     label: "Royal Sunset Hotel",     count: 80  },
  { value: "central-business", label: "Central Business Hotel", count: 80  },
  { value: "grand-palace",     label: "Grand Palace Resort",    count: 410 },
  { value: "premier-inn2",      label: "Premier Inn2",            count: 520 },
  { value: "plaza-hotel2",      label: "Plaza Hotel",            count: 480 },
  { value: "royal-sunset2",     label: "Royal Sunset Hotel2",     count: 80  },
  { value: "central-business2", label: "Central Business Hotel2", count: 80  },
  { value: "grand-palace2",     label: "Grand Palace Resort2",    count: 410 },
];

const propertyRoles: Record<string, { key: string; count: number }[]> = {
  "premier-inn":      [{ key: "staff", count: 430 }, { key: "manager", count: 70  }, { key: "leadership", count: 20 }],
  "plaza-hotel":      [{ key: "staff", count: 390 }, { key: "manager", count: 70  }, { key: "leadership", count: 20 }],
  "royal-sunset":     [{ key: "manager", count: 80 }],
  "central-business": [{ key: "manager", count: 60 }, { key: "leadership", count: 20 }],
  "grand-palace":     [{ key: "staff", count: 390 }, { key: "leadership", count: 20 }],
};

const groupOptions = [
  { key: "premier-front-desk",   label: "Premier Inn - Front Desk",            count: 180, propertyKey: "premier-inn"      },
  { key: "premier-housekeeping", label: "Premier Inn - Housekeeping",          count: 220, propertyKey: "premier-inn"      },
  { key: "premier-food",         label: "Premier Inn - Food & Beverage",       count: 120, propertyKey: "premier-inn"      },
  { key: "plaza-concierge",      label: "Plaza Hotel - Concierge",             count: 90,  propertyKey: "plaza-hotel"      },
  { key: "plaza-events",         label: "Plaza Hotel - Events",                count: 200, propertyKey: "plaza-hotel"      },
  { key: "plaza-security",       label: "Plaza Hotel - Security",              count: 190, propertyKey: "plaza-hotel"      },
  { key: "royal-spa",            label: "Royal Sunset - Spa & Wellness",       count: 40,  propertyKey: "royal-sunset"     },
  { key: "royal-restaurant",     label: "Royal Sunset - Restaurant",           count: 40,  propertyKey: "royal-sunset"     },
  { key: "central-it",           label: "Central Business - IT Support",       count: 30,  propertyKey: "central-business" },
  { key: "central-reception",    label: "Central Business - Reception",        count: 50,  propertyKey: "central-business" },
  { key: "grand-leisure",        label: "Grand Palace - Leisure & Recreation", count: 210, propertyKey: "grand-palace"     },
  { key: "grand-vip",            label: "Grand Palace - VIP Services",         count: 200, propertyKey: "grand-palace"     },
];

const Step3Recipients: React.FC<StepProps> = ({ onBack, onNext, isMobile}) => {
  const [selectedMethod, setSelectedMethod] = useState<SelectionMethod>("");
  const [organizationSelected, setOrganizationSelected] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string | undefined>(undefined);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [propertySearchQuery, setPropertySearchQuery] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [groupSearchQuery, setGroupSearchQuery] = useState("");

  const handleMethodChange = (method: SelectionMethod) => {
    setSelectedMethod(method);
    setOrganizationSelected(false);
    setSelectedRoles([]);
    setSelectedProperty(undefined);
    setRoleSearchQuery("");
    setSelectedProperties([]);
    setPropertySearchQuery("");
    setSelectedGroups([]);
    setGroupSearchQuery("");
  };

  const isNextDisabled = !selectedMethod
    ? true
    : selectedMethod === "organization"
    ? !organizationSelected
    : selectedMethod === "role"
    ? selectedRoles.length === 0
    : selectedMethod === "property"
    ? selectedProperties.length === 0
    : selectedMethod === "group"
    ? selectedGroups.length === 0
    : false; 


  return (
    <>
      <div className="step-card phishing-step3">
        <div className="card-header">
          <div className="card-title">Full Campaign Recipients</div>
          <div className="card-subtitle">
            Select the recipients who will receive the full phishing campaign
            once the test is completed.
          </div>
        </div>

        <div className="phishing-step3__selection">
          <div className="phishing-step3__label">
            Selection method <span className="required">*</span>
          </div>

          {isMobile ? (
            <div className="phishing-step3__dropdown--mobile">
              <Select
                style={{ width: "100%" }}
                value={selectedMethod || undefined}
                dropdownClassName="campaign-setup-filter-option-select"
                onChange={(value) => handleMethodChange(value)}
                options={selectionOptions.map((opt) => ({
                  value: opt.key,
                  label: opt.label,
                }))}
                placeholder="Select a method"
                size="large"
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </div>
          ) : (
            <div className="phishing-step3__grid">
              {selectionOptions.map((opt) => {
                const active = selectedMethod === opt.key;
                return (
                  <div
                    key={opt.key}
                    onClick={() => handleMethodChange(opt.key)}
                    className={`phishing-step3__selector ${
                      active ? "phishing-step3__selector--active" : ""
                    }`}
                  >
                    <div className="phishing-step3__selector-header">
                      <div className="phishing-step3__icon">{opt.icon}</div>
                      <span className="phishing-step3__selector-title">
                        {opt.label}
                      </span>
                    </div>
                    <span className="phishing-step3__selector-desc">
                      {opt.description}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedMethod === "organization" && (
          <OrganizationRecipients
            organizationSelected={organizationSelected}
            setOrganizationSelected={setOrganizationSelected}
          />
        )}

        {selectedMethod === "role" && (
          <RoleRecipients
            propertyOptions={propertyOptions}
            roleOptions={roleOptions}
            propertyRoles={propertyRoles}
            selectedRoles={selectedRoles}
            setSelectedRoles={setSelectedRoles}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            roleSearchQuery={roleSearchQuery}
            setRoleSearchQuery={setRoleSearchQuery}
          />
        )}

        {selectedMethod === "property" && (
          <PropertyRecipients
            propertyOptions={propertyOptions}
            selectedProperties={selectedProperties}
            setSelectedProperties={setSelectedProperties}
            propertySearchQuery={propertySearchQuery}
            setPropertySearchQuery={setPropertySearchQuery}
          />
        )}

        {selectedMethod === "group" && (
          <GroupRecipients
            propertyOptions={propertyOptions}
            groupOptions={groupOptions}
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
            groupSearchQuery={groupSearchQuery}
            setGroupSearchQuery={setGroupSearchQuery}
          />
        )}

        <div className="alert-section">
          <Alert
            title="Information"
            subtitle="No emails will be sent to these recipients until the test is completed and the full campaign is approved"
          />
        </div>
      </div>

      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextBtnDisabled={isNextDisabled}
      />
    </>
  );
};

export default Step3Recipients;